import { useEffect, useState } from "react";
import { featuresApi } from "../api/featuresApi";
import {
  DrawerTabs,
  EntityDiscussionsTab,
  EntityAttachmentsTab,
} from "../../common/components";
import {
  IP_TYPE_OPTIONS,
  SUB_FEATURE_FLAGS,
  DEFAULT_REQUIRED_TEAM_KEYWORDS,
} from "../constants/featuresConstants";
import "../../../V2SideDrawer.css";
import "../../../DefectsViewV2.css";

const EMPTY_SUB_FORM = {
  sub_feature_code: "",
  sub_feature_name: "",
  description: "",
  ip_type: "Default",
  is_BOD: false,
  is_scheduled: false,
  is_EOD: false,
  is_internal: false,
  effort_estimated_hours: 2,
  effort_estimated_minutes: 0,
  effort_phase: "",
  effort_notes: "",
};

function mapSubToForm(sf) {
  if (!sf) return EMPTY_SUB_FORM;
  return { ...EMPTY_SUB_FORM, ...sf };
}

export default function SubFeatureDrawer({
  mode = "create",
  subFeature,
  featureId,
  onClose,
  onSaved,
  showToast,
}) {
  const isEdit = mode === "edit";

  const [form, setForm] = useState(() => mapSubToForm(subFeature));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");

  // Team mapping (Implementation Teams section)
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [teamSel, setTeamSel] = useState({}); // { team_id: bool }

  // Re-sync form when parent passes a different sub-feature
  useEffect(() => {
    setForm(mapSubToForm(subFeature));
  }, [subFeature?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Load teams and seed defaults (only for create flow)
  useEffect(() => {
    let cancelled = false;
    setTeamsLoading(true);
    featuresApi.listTeams()
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setTeams(list);
        if (!isEdit) {
          const defaults = {};
          list.forEach((t) => {
            const name = String(t.team_name || "").toLowerCase();
            defaults[t.id] = DEFAULT_REQUIRED_TEAM_KEYWORDS.some((kw) => name.includes(kw));
          });
          setTeamSel(defaults);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setTeamsLoading(false); });
    return () => { cancelled = true; };
  }, [isEdit]);

  // Load existing team-mapping for edit flow
  useEffect(() => {
    if (!isEdit || !subFeature?.id) return;
    let cancelled = false;
    featuresApi.getSubFeatureTeamMapping(subFeature.id)
      .then((data) => {
        if (cancelled) return;
        if (!Array.isArray(data)) return;
        const sel = {};
        data.forEach((t) => { sel[t.team_id] = !!t.is_required; });
        setTeamSel(sel);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isEdit, subFeature?.id]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggleFlag = (key) => set(key, !form[key]);

  async function handleSave() {
    setError("");
    if (!form.sub_feature_name.trim()) {
      setError("Sub-feature name is required.");
      return;
    }
    if (!isEdit && !featureId) {
      setError("Parent feature is required.");
      return;
    }

    setSaving(true);
    try {
      let sfId;
      if (isEdit) {
        await featuresApi.updateSubFeature(subFeature.id, form);
        sfId = subFeature.id;
      } else {
        // Code is auto-assigned server-side on create; strip from payload.
        const { sub_feature_code: _omit, ...body } = form;
        const created = await featuresApi.createSubFeature({ ...body, feature_id: featureId });
        sfId = created?.id;
      }

      // Persist team mapping (non-fatal on failure — the sub-feature itself saved fine)
      if (sfId && teams.length > 0) {
        const mappings = teams.map((t) => ({
          team_id: t.id,
          is_required: !!teamSel[t.id],
        }));
        try {
          await featuresApi.putSubFeatureTeamMapping(sfId, mappings);
        } catch {
          showToast?.("Sub-feature saved, but team mapping failed", "warn");
        }
      }

      showToast?.(isEdit ? "Sub-feature updated" : "Sub-feature created", "success");
      onSaved?.(sfId);
    } catch (err) {
      setError(err?.message || "Failed to save sub-feature");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="v2drw-scrim" onClick={onClose} />
      <div className="v2drw ftv2-drw" role="dialog" aria-modal="true">
        <div className="v2drw-head">
          <div className="v2drw-head-main">
            <div className="v2drw-eyebrow">Sub-feature</div>
            <div className="v2drw-title">
              {isEdit ? (form.sub_feature_name || "Edit sub-feature") : "New sub-feature"}
            </div>
            {isEdit && form.sub_feature_code && (
              <div className="v2drw-meta">
                <span className="ftv2-code ftv2-code--sub">{form.sub_feature_code}</span>
              </div>
            )}
            {!isEdit && (
              <div className="v2drw-meta">
                <span>Code will be assigned automatically</span>
              </div>
            )}
          </div>
          <button className="v2drw-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {error && <div className="v2drw-msg warn">{error}</div>}

        {isEdit && (
          <DrawerTabs
            tabs={[
              { key: "overview",    label: "Overview",    icon: "overview"    },
              { key: "discussions", label: "Discussions", icon: "discussions" },
              { key: "attachments", label: "Attachments", icon: "attachments" },
            ]}
            activeTab={tab}
            onChange={setTab}
          />
        )}

        <div className="v2drw-body">
          {(!isEdit || tab === "overview") && (
          <div className="ftv2-form">
            <div className="ftv2-form-fld">
              <label className="ftv2-form-label">Sub-feature Name *</label>
              <input
                className="ftv2-form-input"
                value={form.sub_feature_name}
                onChange={(e) => set("sub_feature_name", e.target.value)}
                placeholder="e.g. SSO Login"
                autoFocus={!isEdit}
              />
            </div>

            <div className="ftv2-form-fld">
              <label className="ftv2-form-label">Description</label>
              <textarea
                className="ftv2-form-textarea"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                rows={3}
                placeholder="Brief description…"
              />
            </div>

            <div className="ftv2-form-row">
              <div className="ftv2-form-fld">
                <label className="ftv2-form-label">IP Type</label>
                <select
                  className="ftv2-form-select"
                  value={form.ip_type}
                  onChange={(e) => set("ip_type", e.target.value)}
                >
                  {IP_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="ftv2-form-fld">
                <label className="ftv2-form-label">Flags</label>
                <div className="ftv2-chip-toggles">
                  {SUB_FEATURE_FLAGS.map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      className={`ftv2-chip-toggle${form[key] ? " is-on" : ""}`}
                      onClick={() => toggleFlag(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {!isEdit && (
              <div className="ftv2-form-row">
                <div className="ftv2-form-fld">
                  <label className="ftv2-form-label">
                    Est. Hours
                    <span className="ftv2-label-hint">0–160</span>
                  </label>
                  <input
                    type="number"
                    className="ftv2-form-input"
                    min={0}
                    max={160}
                    step={1}
                    value={form.effort_estimated_hours}
                    onChange={(e) => set("effort_estimated_hours",
                      Math.min(160, Math.max(0, parseInt(e.target.value) || 0))
                    )}
                  />
                </div>
                <div className="ftv2-form-fld">
                  <label className="ftv2-form-label">
                    Est. Minutes
                    <span className="ftv2-label-hint">0–59</span>
                  </label>
                  <input
                    type="number"
                    className="ftv2-form-input"
                    min={0}
                    max={59}
                    step={1}
                    value={form.effort_estimated_minutes}
                    onChange={(e) => set("effort_estimated_minutes",
                      Math.min(59, Math.max(0, parseInt(e.target.value) || 0))
                    )}
                  />
                </div>
              </div>
            )}

            {/* Implementation Teams */}
            <div className="ftv2-section-rule">Implementation Teams</div>
            {teamsLoading && (
              <div className="ftv2-section-loading">Loading teams…</div>
            )}
            {!teamsLoading && teams.length === 0 && (
              <div className="ftv2-section-empty">No teams defined yet — add teams in the Users &amp; Teams panel.</div>
            )}
            {!teamsLoading && teams.length > 0 && (
              <div className="ftv2-team-grid">
                {teams.map((t) => (
                  <label key={t.id} className="ftv2-team-row">
                    <input
                      type="checkbox"
                      checked={!!teamSel[t.id]}
                      onChange={(e) => setTeamSel((s) => ({ ...s, [t.id]: e.target.checked }))}
                    />
                    <div className="ftv2-team-info">
                      <span className="ftv2-team-name">{t.team_name}</span>
                      {t.team_lead_name && (
                        <span className="ftv2-team-lead">Lead: {t.team_lead_name}</span>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
          )}

          {isEdit && tab === "discussions" && (
            <EntityDiscussionsTab
              entityType="sub_feature"
              entityId={subFeature?.id}
              showToast={showToast}
            />
          )}

          {isEdit && tab === "attachments" && (
            <EntityAttachmentsTab
              refType="sub_feature"
              refId={subFeature?.id}
              caption="Sub-feature"
              showToast={showToast}
            />
          )}
        </div>

        <div className="v2drw-foot">
          <span className="v2drw-foot-spacer" />
          <button className="v2drw-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          {(!isEdit || tab === "overview") && (
            <button className="v2drw-btn-save" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create sub-feature"}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
